import { makeAutoObservable, runInAction } from "mobx";
import {
    IEmailAttachment,
    IRmaActionEmailNotificationStore,
    IRmaApproveReturnMethodChangeStore,
    IRmaApproveRequest,
    PAYMENT_STATUS,
} from "../../rma-types";
import { RETURN_METHOD_TYPE } from "../../../../return-methods/return-method-types";
import RmaPageStore from "../rma-page-store";

class RmaApproveActionStore implements IRmaActionEmailNotificationStore, IRmaApproveReturnMethodChangeStore {
	private _model: Partial<IRmaApproveRequest> = {};
    private _isProcessing: boolean = false;
    private _returnMethodChangeConfirmed: boolean = false;

    constructor(private _rma: RmaPageStore, private _actionCallback: (data: IRmaApproveRequest) => void) {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    public get returnMethodWarning(): string|null {
        if (this._rma.customerCaseStore?.shouldCreateFeeDraftOrder) {

            /** when changed RM to no shipment required */
            if (this.newReturnMethodId !== undefined && this._isNoShipmentRMType(this.newReturnMethodId)) {
                return 'FEES_WILL_NOT_BE_COLLECTED';
            }

            /** when for any reason we did not yet sent invoice from created draf order (e.g. the draft-order was not released) */
            return 'MANUALLY_SEND_FEES_INVOICE';
        }
        
        return null;
    }

    private get selectedReturnMethod(): number|undefined {
        return this._rma.rmaReturnMethod?.id;
    }
    public get newReturnMethodId(): number|undefined { return this._model.new_return_method_id ?? this.selectedReturnMethod; }
    public get sendEmailNotification(): boolean { return this._model.send_email_notification ?? this.hasCustomerEmail(); }
    public get emailAdditionalContent(): string { return this._model.email_additional_content ?? ''; }
    public get emailAttachments(): IEmailAttachment[] { return this._model.email_attachment ?? []; }
    public get cancelReturnLabelFee(): boolean { return this._model.cancel_return_label_fee ?? this._getCancelReturnLabelFee(); }
    public get isProcessing(): boolean { return this._isProcessing; }

    public setSendEmailNotification(v: boolean) {
        this._model.send_email_notification = v;
    }

    public setEmailAdditionalContent(v: string) {
        this._model.email_additional_content = v;
    }

    public setEmailAttachments(v: IEmailAttachment[]) {
        this._model.email_attachment = v;
    }

    private _setCancelReturnLabelFee(v: boolean) {
        this._model.cancel_return_label_fee = v;
    }

    public setNewReturnMethodId(v: number) {
        this._returnMethodChangeConfirmed = false;
        this._model.new_return_method_id = v;
        this._alignCancelReturnLabelFee();
    }

    public addEmailAttachment(attachment: IEmailAttachment) {
        if (!this._model.email_attachment) {
            this._model.email_attachment = [];
        }
        this._model.email_attachment.push(attachment);
    }

    public removeEmailAttachment(attachment: IEmailAttachment) {
        if (!this._model.email_attachment) {
            return;
        }
        const index = this._model.email_attachment.indexOf(attachment);
        if (index > -1) {
            this._model.email_attachment.splice(index, 1);
        }
    }

    public hasCustomerEmail(): boolean {
        return Boolean(this._rma.orderStore?.hasCustomerEmail);
    }

    private requestModel(): IRmaApproveRequest {
        return {
            /* Should be true by default but false if no customer or no email */
            send_email_notification: this.sendEmailNotification,
            email_attachment: this.emailAttachments,
            email_additional_content: this.emailAdditionalContent,
            new_return_method_id: this.newReturnMethodId,
            cancel_return_label_fee: this.cancelReturnLabelFee
        };
    }

    public async submitAction(closeCb?: () => void) {
        try {
            this._isProcessing = true;
            await this._actionCallback(this.requestModel());
            runInAction(() => {
                this._isProcessing = false;
                if (closeCb) {
                    closeCb();
                }
            });
        }
        catch (e) {
            runInAction(() => {
                this._isProcessing = false;
            });
        }    
    }

    public get showReturnMethodChangeWarning(): boolean {
        return this.newReturnMethodId !== this.selectedReturnMethod &&
            this._rma.shouldReactOnReturnMethodChange &&
            !this._returnMethodChangeConfirmed;
    }

    public resetReturnMethodChange() {
        this._model.new_return_method_id = this.selectedReturnMethod;
        this._returnMethodChangeConfirmed = false;
    }

    public confirmReturnMethodChange() {
        this._returnMethodChangeConfirmed = true;
    }

    public confirmReturnMethodsChangeWithLabelFees() {
        this._setCancelReturnLabelFee(false);
        this.confirmReturnMethodChange();
    }

    public confirmReturnMethodsChangeWithoutLabelFees() {
        this._setCancelReturnLabelFee(true);
        this.confirmReturnMethodChange();
    }

    private get _rmaHasPayment(): boolean {
        const rmaPayment = this._rma.customerCaseStore?.payments.find(p => {
            let paymentBreakdown;
            try {
                paymentBreakdown = JSON.parse(p.payment_breakdown);
            }
            catch (e) {
                /** should never happen */
                console.error(`Error: could not parse payment breakdown: ${p.payment_breakdown}`);
            }

            /** 
             * We should consider all payments, including cancelled
             * The only exception is when payment was cancelled due to some interrupt in approval process,
             * we do not consider such payments. And the way we identify those interrupted cancelled payments is 
             * that such payments do not have cancel date
             */
            return p.status !== PAYMENT_STATUS.CANCELED || paymentBreakdown.cancelDate !== undefined;
        });

        if (rmaPayment) {
            return true;
        }

        return false;
    }

    private get _rmaHasDraftFeeOrder(): boolean {
        const drafrFeeOrder = this._rma.customerCaseStore?.feeOrder?.draftOrderId;

        if (drafrFeeOrder) {
            return true;
        }

        return false;
    }

    private _isNoShipmentRMType(returnMethodId: number): boolean {
        const foundRm = this._rma.returnMethodPresets.find(rm => {
            return (rm.id === returnMethodId && rm.type === RETURN_METHOD_TYPE.NO_SHIPMENT_REQUIRED);
        });
        return !!foundRm;
    }

    /** 
     * For no shipment required return methods we should cancel return label fee.
     * This method is called when we created store and change return method in action modal
     */
    private _getCancelReturnLabelFee(): boolean {
        return this.newReturnMethodId !== undefined && this._isNoShipmentRMType(this.newReturnMethodId);
    }

    private _alignCancelReturnLabelFee() {
        this._setCancelReturnLabelFee(this._getCancelReturnLabelFee());
    }
}

export default RmaApproveActionStore;